import { PencilLine, Save, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { CompactDateInput } from '@/components/ui/date-input'
import { CompactFormInput, FormCheckboxField, FormErrorBanner } from '@/components/ui/form'
import { Modal, ModalFooter } from '@/components/ui/modal'
import { cn } from '@/lib/utils'

// 一括更新で変更可能な項目。v1 MillsheetsForm.tsx と同じ5項目（工区・節・部位は配下の全明細へ反映される）。
// submissionDate だけ null を許容する（クリアして「提出日なし」に一括更新できるようにする）
export type MillsheetBulkEditFields = {
  submissionDate?: string | null
  constructionArea?: string
  member?: string
  buildingPart?: string
  innerNo?: string
}

type FieldKey = 'submissionDate' | 'constructionArea' | 'member' | 'buildingPart' | 'innerNo'

const FIELD_LABELS: Record<FieldKey, string> = {
  submissionDate: '提出日',
  constructionArea: '工区',
  member: '節',
  buildingPart: '部位',
  innerNo: '内部番号',
}

// 「チェックを付けないと入力できない」ことが opacity-50 だけでは伝わりにくかったFBを受け、
// 無効時は背景・文字色・枠線でも編集不可を示す。border-input（色）はそのまま残し
// border-dashed（形状）だけ足すことで、有効時と混ざらない見た目にしている
const DISABLED_FIELD_CLASS = 'pointer-events-none border-dashed bg-muted text-muted-foreground'
// 提出日（DateInput）だけは、無効時に「クリックして編集」を入力欄の上に重ねて見せるため、
// ネイティブの「年/月/日」表示（空値時のプレースホルダ相当）を隠す必要がある。
// text-muted-foreground だと透けて重なった二重表示になるので、ここだけ text-transparent にする
// （bg-muted / border-dashed は他の項目と揃える）
const DISABLED_DATE_FIELD_CLASS = 'pointer-events-none border-dashed bg-muted text-transparent'
const DISABLED_FIELD_HINT = 'クリックすると編集できます'

// v1 の antd Form は「未入力＝undefined（更新しない）」「入力して空にした＝""（空文字で更新）」という
// touched 判定でこの区別を暗黙に行っていたが、素の input ではその区別が付かないため、
// チェックボックスで「この項目を変更する」ことを明示させる設計にしている
export function BulkEditDialog({
  open,
  selectedCount,
  onClose,
  onSubmit,
}: {
  open: boolean
  selectedCount: number
  onClose: () => void
  onSubmit: (fields: MillsheetBulkEditFields) => void
}) {
  if (!open) {
    return null
  }
  return <BulkEditDialogContent selectedCount={selectedCount} onClose={onClose} onSubmit={onSubmit} />
}

function BulkEditDialogContent({
  selectedCount,
  onClose,
  onSubmit,
}: {
  selectedCount: number
  onClose: () => void
  onSubmit: (fields: MillsheetBulkEditFields) => void
}) {
  const [enabled, setEnabled] = useState<Record<FieldKey, boolean>>({
    submissionDate: false,
    constructionArea: false,
    member: false,
    buildingPart: false,
    innerNo: false,
  })
  const [values, setValues] = useState<Record<FieldKey, string>>({
    submissionDate: '',
    constructionArea: '',
    member: '',
    buildingPart: '',
    innerNo: '',
  })

  const toggle = (key: FieldKey) => {
    setEnabled((current) => ({ ...current, [key]: !current[key] }))
  }

  // 無効な入力欄をクリックしたときに「チェックを付けて、そのまま入力欄にフォーカスする」ため、
  // 各行の入力欄をラップする要素への ref を持つ。有効化後に querySelector('input') で
  // 実 DOM の <input> を取り、そこへフォーカスを移す（CompactFormInput / CompactDateInput は
  // ref を受け取れる作りではないため、要素そのものを ref で辿る）
  const fieldWrapperRefs = useRef<Record<FieldKey, HTMLDivElement | null>>({
    submissionDate: null,
    constructionArea: null,
    member: null,
    buildingPart: null,
    innerNo: null,
  })
  // setState は非同期なので、「有効化直後にどの欄へフォーカスすべきか」を ref に持ち、
  // disabled が外れた後の再レンダーを検知する useEffect 側で消費する
  const pendingFocusKeyRef = useRef<FieldKey | null>(null)

  // チェックボックスではなく入力欄側をクリックしたときの有効化。既に有効な欄への
  // クリックはチェックを外す方向には倒さない（無効→有効の一方向。トグルはチェックボックス側の役割のまま）
  const enableFieldAndFocus = (key: FieldKey) => {
    if (enabled[key]) {
      return
    }
    pendingFocusKeyRef.current = key
    setEnabled((current) => ({ ...current, [key]: true }))
  }

  useEffect(() => {
    const key = pendingFocusKeyRef.current
    if (key === null) {
      return
    }
    pendingFocusKeyRef.current = null
    fieldWrapperRefs.current[key]?.querySelector('input')?.focus()
  }, [enabled])

  // 一度でも更新を押したら true。それまでは何もチェックしていなくてもエラーにせず、以後はチェックを付けた時点でエラーを消す
  const [attempted, setAttempted] = useState(false)
  const firstCheckboxRef = useRef<HTMLDivElement>(null)

  const handleSubmit = () => {
    setAttempted(true)
    if (!Object.values(enabled).some((x) => x)) {
      // 項目に結び付かないエラーなので、最初のチェックボックスへフォーカスを移して次の操作を促す
      requestAnimationFrame(() => {
        firstCheckboxRef.current?.querySelector('input')?.focus()
      })
      return
    }
    const fields: MillsheetBulkEditFields = {
      ...(enabled.submissionDate && { submissionDate: values.submissionDate === '' ? null : values.submissionDate }),
      ...(enabled.constructionArea && { constructionArea: values.constructionArea }),
      ...(enabled.member && { member: values.member }),
      ...(enabled.buildingPart && { buildingPart: values.buildingPart }),
      ...(enabled.innerNo && { innerNo: values.innerNo }),
    }
    onSubmit(fields)
  }

  const hasAnyEnabled = Object.values(enabled).some((x) => x)

  return (
    <Modal
      size="md"
      title="ミルシートの一括更新"
      description={`選択中の${selectedCount}件のミルシートを一括更新します。変更する項目だけチェックしてください。`}
      icon={<PencilLine className="size-4" />}
      onClose={onClose}
    >
      {/* 工区・節・部位は明細の属性。一括更新は明細単位ではなくミルシート単位で行うため、
          明細ごとに別々だった値も上書きされることを事前に伝える */}
      <p className="mt-3 text-xs text-muted-foreground" data-testid="millsheet-bulk-edit-item-note">
        工区・節・部位は、選択したミルシート配下の全明細に反映されます（明細ごとに異なる値も上書きされます）。
      </p>
      <div className="mt-4 space-y-3">
        {(['submissionDate', 'constructionArea', 'member', 'buildingPart', 'innerNo'] as const).map((key) => {
          const isEnabled = enabled[key]
          return (
            <div
              key={key}
              ref={key === 'submissionDate' ? firstCheckboxRef : undefined}
              className="flex items-center gap-3"
            >
              <FormCheckboxField
                label={FIELD_LABELS[key]}
                className="w-40 shrink-0 px-3 py-2"
                checked={isEnabled}
                onChange={() => {
                  toggle(key)
                }}
                data-testid={`millsheet-bulk-edit-enable-${key}`}
              />
              {/* 無効な入力欄クリックでチェックを付けて有効化するためのラッパー。
                  disabled な <input> はクリックイベントを発火しないので、無効時は入力欄に
                  pointer-events-none を当てて、代わりにこのラッパーで onClick を受ける */}
              <div
                ref={(el) => {
                  fieldWrapperRefs.current[key] = el
                }}
                onClick={() => {
                  enableFieldAndFocus(key)
                }}
                title={isEnabled ? undefined : DISABLED_FIELD_HINT}
                data-testid={`millsheet-bulk-edit-field-${key}`}
                className={cn('flex flex-1 items-center gap-2', !isEnabled && 'cursor-pointer')}
              >
                {/* 日付は共通の DateInput に寄せてクリックでカレンダーが開く挙動を全画面でそろえるため */}
                {key === 'submissionDate' ? (
                  // 他の4項目（CompactFormInput）は入力欄の中に placeholder を出せるので
                  // 入力欄1つが行いっぱいに広がるが、type="date" は placeholder が効かないため
                  // ヒントを別要素で出す必要がある。ヒントを入力欄の外（隣）に置くと、その分だけ
                  // 入力欄が他の4項目より狭くなり左右端が揃わなくなる（2026/08/30 差分レビューで発覚）。
                  // このラッパーだけを flex-1 にして入力欄を行いっぱいまで広げ、ヒントは
                  // absolute で入力欄の上に重ねることで、他の項目と同じ「入力欄の中に見える」
                  // 読み味と、左右端が揃う幅の両方を成立させる
                  <div className="relative flex-1">
                    <CompactDateInput
                      value={values[key]}
                      disabled={!isEnabled}
                      onChange={(e) => {
                        setValues((current) => ({ ...current, [key]: e.target.value }))
                      }}
                      aria-label={FIELD_LABELS[key]}
                      data-testid={`millsheet-bulk-edit-input-${key}`}
                      className={cn(!isEnabled && DISABLED_DATE_FIELD_CLASS)}
                    />
                    {/* ネイティブの「年/月/日」表示は DISABLED_DATE_FIELD_CLASS の
                        text-transparent で隠しているので、重なって読みにくくなることはない。
                        pointer-events-none にして、下の入力欄・行のクリックで有効化する操作を妨げない */}
                    <span
                      aria-hidden={isEnabled}
                      className={cn(
                        'pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs font-medium text-muted-foreground transition-opacity',
                        isEnabled ? 'opacity-0' : 'opacity-100',
                      )}
                    >
                      クリックして編集
                    </span>
                  </div>
                ) : (
                  <CompactFormInput
                    type="text"
                    value={values[key]}
                    disabled={!isEnabled}
                    onChange={(e) => {
                      setValues((current) => ({ ...current, [key]: e.target.value }))
                    }}
                    aria-label={FIELD_LABELS[key]}
                    placeholder={isEnabled ? undefined : 'クリックして編集'}
                    data-testid={`millsheet-bulk-edit-input-${key}`}
                    className={cn(!isEnabled && DISABLED_FIELD_CLASS)}
                  />
                )}
              </div>
            </div>
          )
        })}
      </div>

      {attempted && !hasAnyEnabled && (
        <FormErrorBanner
          message="変更する項目にチェックを付けて、新しい値を入力してください。"
          testId="millsheet-bulk-edit-error"
          className="mt-4"
        />
      )}

      <ModalFooter className="mt-5">
        <Button variant="outline" onClick={onClose}>
          <X data-icon="inline-start" />
          キャンセル
        </Button>
        <Button data-testid="millsheet-bulk-edit-submit" disabled={selectedCount === 0} onClick={handleSubmit}>
          <Save data-icon="inline-start" />
          {selectedCount}件を一括更新
        </Button>
      </ModalFooter>
    </Modal>
  )
}
